import { trackEvent } from './analytics';

/**
 * Voice interaction and call analytics utilities
 * Tracks voice testing, calls, and performance metrics
 */
class VoiceAnalyticsManager {
  constructor() {
    this.callSessions = new Map();
    this.testSessions = new Map();
    this.performanceMetrics = {
      audioLatency: [],
      synthesisTime: [],
      callQuality: []
    };
  }

  /**
   * Track voice test initiation
   */
  trackVoiceTestStarted(testId, voiceId, agentId = null) {
    const session = {
      testId,
      voiceId,
      agentId,
      startTime: Date.now(),
      endTime: null,
      success: null,
      errorType: null,
      audioGenerated: false,
      playbackStarted: false,
      playbackCompleted: false,
      userFeedback: null
    };

    this.testSessions.set(testId, session);

    trackEvent('voice_test_started', {
      test_id: testId,
      voice_id: voiceId,
      agent_id: agentId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track test agent button click (Analytics.md compliance)
   */
  trackTestAgentButtonClicked(agentId, source = 'agent_creation') {
    trackEvent('test_agent_button_clicked', {
      agent_id: agentId,
      source: source,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track start call page viewed (Analytics.md compliance)
   */
  trackStartCallPageViewed(agentId) {
    trackEvent('start_call_page_viewed', {
      agent_id: agentId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track start call button clicked (Analytics.md compliance)
   */
  trackStartCallButtonClicked(agentId) {
    trackEvent('start_call_button_clicked', {
      agent_id: agentId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track voice call initiated (Analytics.md compliance)
   */
  trackVoiceCallInitiated(callId, agentId) {
    trackEvent('voice_call_initiated', {
      call_id: callId,
      agent_id: agentId,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track voice call connected (Analytics.md compliance)
   */
  trackVoiceCallConnected(callId, agentId, connectionTime, connectionMethod = 'WebRTC') {
    trackEvent('voice_call_connected', {
      call_id: callId,
      agent_id: agentId,
      connection_time: connectionTime,
      connection_method: connectionMethod,
      audio_quality: 'good', // This would be measured
      user_device: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track voice call ended (Analytics.md compliance)
   */
  trackVoiceCallEnded(callId, agentId, duration, terminationReason, userSatisfaction = null) {
    trackEvent('voice_call_ended', {
      call_id: callId,
      agent_id: agentId,
      duration: duration,
      termination_reason: terminationReason,
      user_satisfaction: userSatisfaction,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track voice synthesis used (Analytics.md compliance)
   */
  trackVoiceSynthesisUsed(agentId, responseCount, totalDuration, averageResponseTime, language, voiceQualityRating) {
    trackEvent('voice_synthesis_used', {
      agent_id: agentId,
      response_count: responseCount,
      total_duration: totalDuration,
      average_response_time: averageResponseTime,
      language: language,
      voice_quality_rating: voiceQualityRating,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track test call feedback (Analytics.md compliance)
   */
  trackTestCallFeedback(testId, agentId, feedback, rating) {
    trackEvent('test_call_feedback', {
      test_id: testId,
      agent_id: agentId,
      feedback: feedback,
      rating: rating,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track voice synthesis completion
   */
  trackVoiceSynthesis(testId, synthesisTime, audioSize, success = true) {
    const session = this.testSessions.get(testId);
    if (!session) return;

    session.audioGenerated = success;
    session.synthesisTime = synthesisTime;
    session.audioSize = audioSize;

    // Track performance
    if (success) {
      this.performanceMetrics.synthesisTime.push(synthesisTime);
    }

    trackEvent('voice_synthesis_completed', {
      test_id: testId,
      voice_id: session.voiceId,
      synthesis_time: synthesisTime,
      audio_size: audioSize,
      success: success,
      avg_synthesis_time: this.getAverageSynthesisTime()
    });
  }

  /**
   * Track audio playback events
   */
  trackAudioPlayback(testId, action, metadata = {}) {
    const session = this.testSessions.get(testId);
    if (!session) return;

    if (action === 'started') {
      session.playbackStarted = true;
    } else if (action === 'completed') {
      session.playbackCompleted = true;
    } else if (action === 'error') {
      session.playbackError = metadata.error;
    }

    trackEvent('voice_playback', {
      test_id: testId,
      voice_id: session.voiceId,
      action: action,
      ...metadata
    });
  }

  /**
   * Track voice test completion
   */
  trackVoiceTestCompleted(testId, success, userFeedback = null, errorType = null) {
    const session = this.testSessions.get(testId);
    if (!session) return;

    const now = Date.now();
    session.endTime = now;
    session.success = success;
    session.userFeedback = userFeedback;
    session.errorType = errorType;

    const duration = Math.round((now - session.startTime) / 1000);

    trackEvent('voice_test_completed', {
      test_id: testId,
      voice_id: session.voiceId,
      agent_id: session.agentId,
      success: success,
      duration: duration,
      audio_generated: session.audioGenerated,
      playback_started: session.playbackStarted,
      playback_completed: session.playbackCompleted,
      user_feedback: userFeedback,
      error_type: errorType
    });

    // Clean up session after delay
    setTimeout(() => {
      this.testSessions.delete(testId);
    }, 300000); // 5 minutes
  }

  /**
   * Track voice call initiation
   */
  trackCallStarted(callId, agentId, initiationType = 'user') {
    const session = {
      callId,
      agentId,
      initiationType,
      startTime: Date.now(),
      endTime: null,
      duration: 0,
      turnCount: 0,
      userSpeakTime: 0,
      agentSpeakTime: 0,
      silenceTime: 0,
      interruptions: 0,
      technicalIssues: [],
      callQuality: null,
      endReason: null,
      userSatisfaction: null
    };

    this.callSessions.set(callId, session);

    trackEvent('voice_call_started', {
      call_id: callId,
      agent_id: agentId,
      initiation_type: initiationType,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent
    });
  }

  /**
   * Track call turn (user or agent speaking)
   */
  trackCallTurn(callId, speaker, duration, content = null) {
    const session = this.callSessions.get(callId);
    if (!session) return;

    session.turnCount++;
    
    if (speaker === 'user') {
      session.userSpeakTime += duration;
    } else if (speaker === 'agent') {
      session.agentSpeakTime += duration;
    }

    trackEvent('voice_call_turn', {
      call_id: callId,
      agent_id: session.agentId,
      turn_number: session.turnCount,
      speaker: speaker,
      duration: duration,
      content_length: content ? content.length : null,
      cumulative_turns: session.turnCount
    });
  }

  /**
   * Track call interruptions
   */
  trackCallInterruption(callId, type, context = null) {
    const session = this.callSessions.get(callId);
    if (!session) return;

    session.interruptions++;

    trackEvent('voice_call_interruption', {
      call_id: callId,
      agent_id: session.agentId,
      interruption_type: type, // user_interrupt, agent_interrupt, technical
      interruption_number: session.interruptions,
      context: context
    });
  }

  /**
   * Track technical issues during call
   */
  trackTechnicalIssue(callId, issueType, severity, resolved = false) {
    const session = this.callSessions.get(callId);
    if (!session) return;

    const issue = {
      type: issueType,
      severity: severity,
      timestamp: Date.now(),
      resolved: resolved
    };

    session.technicalIssues.push(issue);

    trackEvent('voice_call_technical_issue', {
      call_id: callId,
      agent_id: session.agentId,
      issue_type: issueType,
      severity: severity,
      resolved: resolved,
      total_issues: session.technicalIssues.length
    });
  }

  /**
   * Track call quality metrics
   */
  trackCallQuality(callId, qualityMetrics) {
    const session = this.callSessions.get(callId);
    if (!session) return;

    session.callQuality = qualityMetrics;
    this.performanceMetrics.callQuality.push(qualityMetrics.overallScore);

    trackEvent('voice_call_quality', {
      call_id: callId,
      agent_id: session.agentId,
      audio_quality: qualityMetrics.audioQuality,
      latency: qualityMetrics.latency,
      packet_loss: qualityMetrics.packetLoss,
      overall_score: qualityMetrics.overallScore,
      avg_quality: this.getAverageCallQuality()
    });
  }

  /**
   * Track call completion
   */
  trackCallCompleted(callId, endReason, userSatisfaction = null) {
    const session = this.callSessions.get(callId);
    if (!session) return;

    const now = Date.now();
    session.endTime = now;
    session.duration = Math.round((now - session.startTime) / 1000);
    session.endReason = endReason;
    session.userSatisfaction = userSatisfaction;

    const totalSpeakTime = session.userSpeakTime + session.agentSpeakTime;
    const silenceTime = session.duration * 1000 - totalSpeakTime; // Convert to ms

    trackEvent('voice_call_completed', {
      call_id: callId,
      agent_id: session.agentId,
      duration: session.duration,
      turn_count: session.turnCount,
      user_speak_time: session.userSpeakTime,
      agent_speak_time: session.agentSpeakTime,
      silence_time: Math.max(0, silenceTime),
      interruptions: session.interruptions,
      technical_issues: session.technicalIssues.length,
      call_quality: session.callQuality?.overallScore,
      end_reason: endReason,
      user_satisfaction: userSatisfaction,
      engagement_ratio: totalSpeakTime > 0 ? session.userSpeakTime / totalSpeakTime : 0
    });

    // Track conversion if call was successful
    if (endReason === 'completed' && session.duration >= 30) {
      trackEvent('conversion', {
        conversion_type: 'successful_voice_call',
        conversion_value: session.duration,
        agent_id: session.agentId
      });
    }

    // Clean up session after delay
    setTimeout(() => {
      this.callSessions.delete(callId);
    }, 300000); // 5 minutes
  }

  /**
   * Track voice permission request
   */
  trackVoicePermission(granted, error = null) {
    trackEvent('voice_permission_request', {
      granted: granted,
      error: error,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track microphone test
   */
  trackMicrophoneTest(testId, success, volume = null, error = null) {
    trackEvent('microphone_test', {
      test_id: testId,
      success: success,
      volume_level: volume,
      error: error,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track browser compatibility
   */
  trackBrowserCompatibility(features) {
    trackEvent('browser_compatibility_check', {
      web_rtc_supported: features.webRTC,
      media_recorder_supported: features.mediaRecorder,
      audio_context_supported: features.audioContext,
      user_agent: navigator.userAgent,
      browser_name: this.getBrowserName(),
      timestamp: new Date().toISOString()
    });
  }

  // Helper methods
  getAverageSynthesisTime() {
    const times = this.performanceMetrics.synthesisTime;
    return times.length > 0 ? times.reduce((a, b) => a + b) / times.length : 0;
  }

  getAverageCallQuality() {
    const scores = this.performanceMetrics.callQuality;
    return scores.length > 0 ? scores.reduce((a, b) => a + b) / scores.length : 0;
  }

  getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  /**
   * Track voice library access
   */
  trackVoiceLibraryAccess(action, metadata = {}) {
    trackEvent('voice_library_access', {
      action: action,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }

  /**
   * Track voice library load completion
   */
  trackVoiceLibraryLoad(loadMetrics) {
    trackEvent('voice_library_loaded', {
      total_voices: loadMetrics.total_voices,
      voice_types: loadMetrics.voice_types,
      load_time: loadMetrics.load_time || null,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track voice library errors
   */
  trackVoiceLibraryError(errorType, errorMessage = null, metadata = {}) {
    trackEvent('voice_library_error', {
      error_type: errorType,
      error_message: errorMessage,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      ...metadata
    });
  }

  /**
   * Track TTS usage
   */
  trackTTSUsage(voiceId, metadata = {}) {
    trackEvent('tts_usage', {
      voice_id: voiceId,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }

  /**
   * Track TTS generation success
   */
  trackTTSGenerated(voiceId, metadata = {}) {
    trackEvent('tts_generated', {
      voice_id: voiceId,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }

  /**
   * Track TTS errors
   */
  trackTTSError(voiceId, errorMessage, metadata = {}) {
    trackEvent('tts_error', {
      voice_id: voiceId,
      error_message: errorMessage,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }

  /**
   * Track voice selection
   */
  trackVoiceSelection(voiceId, source, metadata = {}) {
    trackEvent('voice_selection', {
      voice_id: voiceId,
      source: source,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }

  /**
   * Track voice generation button click
   */
  trackVoiceGenerationClick(voiceId, userMessage, metadata = {}) {
    trackEvent('voice_generation_click', {
      voice_id: voiceId,
      user_message: userMessage ? userMessage.substring(0, 100) : null, // First 100 chars for privacy
      message_length: userMessage ? userMessage.length : 0,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }

  /**
   * Track voice interface access
   */
  trackVoiceInterfaceAccess(agentId, source, metadata = {}) {
    trackEvent('voice_interface_access', {
      agent_id: agentId,
      source: source,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      ...metadata
    });
  }

  /**
   * Track call initiation
   */
  trackCallInitiated(agentId, metadata = {}) {
    trackEvent('call_initiated', {
      agent_id: agentId,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }

  /**
   * Track call connection
   */
  trackCallConnected(agentId, metadata = {}) {
    trackEvent('call_connected', {
      agent_id: agentId,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }

  /**
   * Track conversation events
   */
  trackConversationEvent(agentId, eventType, metadata = {}) {
    trackEvent('conversation_event', {
      agent_id: agentId,
      event_type: eventType,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }

  /**
   * Track call ended
   */
  trackCallEnded(agentId, metadata = {}) {
    trackEvent('call_ended', {
      agent_id: agentId,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }

  /**
   * Track call control usage
   */
  trackCallControlUsed(agentId, controlType, metadata = {}) {
    trackEvent('call_control_used', {
      agent_id: agentId,
      control_type: controlType,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }

  /**
   * Track interaction mode change
   */
  trackInteractionModeChange(newMode, metadata = {}) {
    trackEvent('interaction_mode_change', {
      new_mode: newMode,
      timestamp: new Date().toISOString(),
      ...metadata
    });
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary() {
    return {
      avgSynthesisTime: this.getAverageSynthesisTime(),
      avgCallQuality: this.getAverageCallQuality(),
      totalTests: this.testSessions.size,
      totalCalls: this.callSessions.size,
      activeSessions: this.callSessions.size + this.testSessions.size
    };
  }

  /**
   * Clean up old sessions
   */
  cleanup() {
    const now = Date.now();
    const maxAge = 30 * 60 * 1000; // 30 minutes

    // Clean up test sessions
    for (const [testId, session] of this.testSessions.entries()) {
      if (session.endTime && (now - session.endTime) > maxAge) {
        this.testSessions.delete(testId);
      }
    }

    // Clean up call sessions
    for (const [callId, session] of this.callSessions.entries()) {
      if (session.endTime && (now - session.endTime) > maxAge) {
        this.callSessions.delete(callId);
      }
    }
  }
}

// Create singleton instance
const voiceAnalytics = new VoiceAnalyticsManager();

// Run cleanup every 10 minutes
setInterval(() => voiceAnalytics.cleanup(), 10 * 60 * 1000);

export default voiceAnalytics;